import moment from 'moment';

import * as log from '../util/log';
import mixpanel from 'mixpanel-browser';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT, LISTING_STATE_PUBLISHED } from '../util/types';
import { createSlug } from '../util/urlHelpers';
import { util as sdkUtil, createImageVariantConfig } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';

import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { getCollection } from '../util/api';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { updateWallet } from '../containers/CheckoutPage/CheckoutPage.duck';
import { setSessionUser } from '../analytics/analytics';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const MARK_EMAIL_AS_VERIFIED = 'app/user/MARK_EMAIL_AS_VERIFIED';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserInProgress: false,
  currentUserShowError: null,
  currentUserListings: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  sessionEmailVerified: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_EMAIL_AS_VERIFIED:
      return { ...state, sessionEmailVerified: true };

    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserInProgress: true, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS: {
      let incomingUser = payload;
      if (state.sessionEmailVerified && incomingUser && incomingUser.attributes) {
        incomingUser = {
          ...incomingUser,
          attributes: {
            ...incomingUser.attributes,
            emailVerified: true,
            pendingEmail: null,
          }
        };
      }
      return { ...state, currentUserInProgress: false, currentUser: mergeCurrentUser(state.currentUser, incomingUser) };
    }
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserInProgress: false, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings, currentUserListings: payload.listings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

export const markEmailAsVerified = () => ({ type: MARK_EMAIL_AS_VERIFIED });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listings) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 100,
  };

  const getAllPublishedListing = async (totalPage, allListings = []) => {
    allListings.push(sdk.ownListings
      .query({ ...params, page: totalPage })
      .then(res => res));
    if (totalPage > 2) {
      return getAllPublishedListing((totalPage - 1), allListings);
    }
    return await Promise.all(allListings);
  }

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      const hasPublishedListings = hasListings && ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, response.data.data));
      if (hasListings) {
        const { totalPages } = response.data.meta;
        return getAllPublishedListing(totalPages)
          .then(res => {
            const listings = [...response.data.data, ...res.map(l => l.data.data).flat(1)];
            const hasDraftListings = listings && listings.length && listings.filter(listing => ensureOwnListing(listing).attributes.state == LISTING_STATE_DRAFT).length;
            const hasPublishedListings = listings && listings.length && listings.filter(listing => ensureOwnListing(listing).attributes.state == LISTING_STATE_PUBLISHED);

            if (hasDraftListings) {
              mixpanel.track('User-DraftListings', {
                distinct_id: currentUser.id.uuid,
                user_id: currentUser.id.uuid,
                totalDraftListing: hasDraftListings,
                environment: (process.env && process.env.REACT_APP_ENV) || 'development',
              });
            }
            if (hasPublishedListings && hasPublishedListings.length) {
              if (hasPublishedListings[0].attributes.publicData.listingType == 'company') {
                const totalPublishedListingHiring = hasPublishedListings.map(list => list.attributes.publicData.roles).flat();
                mixpanel.track('User-PublishedListings', {
                  distinct_id: currentUser.id.uuid,
                  user_id: currentUser.id.uuid,
                  CompanyHiringPublishedRoles: totalPublishedListingHiring,
                  publishedListing: hasPublishedListings.length,
                  PublishedListingUrls: hasPublishedListings.map(p => (process.env.REACT_APP_CANONICAL_ROOT_URL + createSlug(p.attributes.title) + '/' + p.id.uuid)),
                  environment: (process.env && process.env.REACT_APP_ENV) || 'development',
                });
              } else {
                mixpanel.track('User-PublishedListings', {
                  distinct_id: currentUser.id.uuid,
                  user_id: currentUser.id.uuid,
                  publishedListing: hasPublishedListings.length,
                  PublishedListingUrls: hasPublishedListings.map(p => (process.env.REACT_APP_CANONICAL_ROOT_URL + createSlug(p.attributes.title) + '/' + p.id.uuid)),
                  environment: (process.env && process.env.REACT_APP_ENV) || 'development',
                });
              }
            }
          });
      }
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      const currentUser = getState().user.currentUser;

      // Filter out transactions that have already been seen by the current user
      const unreadTransactions = transactions && transactions?.filter(tx => {
        const lastTransitionedAt = tx?.attributes?.lastTransitionedAt;
        const metadata = tx?.attributes?.metadata || {};
        const customerId = tx?.relationships?.customer?.data?.id?.uuid;
        const currentUserId = currentUser?.id?.uuid;

        // Determine if current user is customer or provider
        const isCustomer = customerId === currentUserId;

        const lastSeen = isCustomer
          ? metadata.lastSeenByCustomerAt
          : metadata.lastSeenByProviderAt;

        // If never seen, or last transition is newer than last seen, it's unread
        return !lastSeen || new Date(lastTransitionedAt) > new Date(lastSeen);
      });

      dispatch(fetchCurrentUserNotificationsSuccess(unreadTransactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchCurrentUser = (params = null, token) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const {
    aspectRatio = 1 / 1,
    variantPrefix = 'listing-card',
  } = {};

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio)
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));

      if (typeof window !== 'undefined' && currentUser) {
        setSessionUser({
          userId: currentUser.id.uuid,
          userType: currentUser.attributes.profile.publicData.accountType,
          userEmail: currentUser.attributes.email
        });
      }
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      mixpanel.people.set_once({
        distinct_id: (currentUser.id && currentUser.id.uuid),
        name: `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`,
        email: currentUser.attributes.email,
        first_name: currentUser.attributes.profile.firstName,
        last_name: currentUser.attributes.profile.lastName,
      });
      mixpanel.identify((currentUser.id && currentUser.id.uuid));
      if (token) {
        const { accountType } = (currentUser && currentUser.id && currentUser.attributes.profile.publicData) || {};
        if (accountType == 'company') {
          try {
            dispatch(updateWallet({
              tx_id: 'WELCOME_CREDIT',
              tokens: 20,
              package: {
                // Removed expireIn from Welcome tokens
                // expiresIn: moment().add(15, 'days').unix()
              },
              type: "CREDIT",
              RNT_TOKEN_ID: 'Welcome credit',
            }));
          } catch (e) {
            console.error(e, '**** WELCOME_CREDIT **** => e');
          }
        }
      }
      try {
        getCollection({
          tableName: "walletHistory",
          query: {
            buyerId: (currentUser && currentUser.id && currentUser.id.uuid)
          },
        })
          .then(res => {
            if (res.data) {
              const data = JSON.parse(res.data);
              const currentSeeds = data.reduce((accu, curr) => {
                if (curr.type == 'CREDIT') {
                  return accu + (curr.tokens || 0);
                }
                return accu - (curr.tokens || 0);
              }, 0);
              Object.assign(currentUser.attributes.profile.protectedData, { currentSeeds });
              dispatch(currentUserShowSuccess(currentUser));
              sdk.currentUser.updateProfile({
                protectedData: { currentSeeds }
              }, { expand: true })
                .then(response => {
                  const entities = denormalisedResponseEntities(response);
                  if (entities.length !== 1) {
                    throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
                  }
                  const currentUserData = entities[0];
                  dispatch(currentUserShowSuccess(currentUserData));
                  return currentUserData;
                });
            }
          })
          .catch(e => console.error(e, '**** getCollection user duck **** => e'));
      } catch (e) {
        console.error(e, '**** getCollection user duck **** => e');
      }
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
